import React, { useContext, useEffect, useReducer, useMemo } from 'react'
import { getSession } from '@fs/zion-session'
import zionDebug from '@fs/zion-debug'
import { doFetchGet, getXRequestId, tempWindowFSRemoteLog } from '../apiService/apiService'
import { getPlacePermissions } from '../apiService/placePermissionService'
import { hasAnyPermissionForFeature } from '../apiService/placePermissionHelpers'
import searchReducer, { DEFAULT_SEARCH_STATE } from '../Search/searchReducer'

const debug = zionDebug('standards:places:PlacesAppContext')

const PlacesAppStateContext = React.createContext()
const PlacesAppDispatchContext = React.createContext()

function placesAppReducer(state, action) {
  switch (action.type) {
    case 'permissions': {
      return { ...state, permissions: action.permissions }
    }
    case 'editMode': {
      return { ...state, editMode: !!action.editMode }
    }
    case 'authComplete': {
      return { ...state, authComplete: action.authComplete }
    }
    // See if we can have the backend use the official maintenance window
    case 'health': {
      return { ...state, health: action.health }
    }
    case 'isDirty': {
      return { ...state, isDirty: action.isDirty }
    }
    case 'SET_HISTORY': {
      return { ...state, history: action.history }
    }
    case 'SET_UI_STATE': {
      return {
        ...state,
        ui: {
          ...state.ui,
          ...action.uiState,
        },
      }
    }

    default: {
      throw new Error(`Unhandled action type: ${action.type}`)
    }
  }
}

async function getHealth() {
  const url = `/service/standards/place/ws-write/healthcheck/status`

  let resp
  let placeServiceHealth = { canRead: false, canWrite: false }

  try {
    resp = await doFetchGet(url, {
      headers: { accept: 'application/json', 'Content-Type': 'application/json', 'X-Request-ID': getXRequestId() },
    })
    const canRead = resp?.data?.solrReadConnected && resp?.data?.dbReadReady
    const canWrite = resp?.data?.solrWriteConnected && resp?.data?.solrWriteReady && resp?.data?.dbWriteReady
    placeServiceHealth = { canRead, canWrite }
  } catch (err) {
    debug('Error while performing standards/place/ws-write/healthcheck/status:', err)
    tempWindowFSRemoteLog(
      `Error while performing standards/place/ws-write/healthcheck/status:${JSON.stringify(err)}`,
      1
    )
  }

  return placeServiceHealth
}

let SendKeepAliveTimer = null
let KeepAliveTimer = null

function keepAliveSession() {
  const SEND_KEEPALIVE_INTERVAL = 300000 // This should be 60 minutes,  5 minutes in milliseconds
  SendKeepAliveTimer = window.setInterval(() => {
    // checking if session is alive also keeps it alive :)
    // User.getSession(true) - this will call get session and ignore the cache.
    const isAlive = !!getSession(true)
    if (!isAlive) {
      debug('#### Session is DEAD ###')
      window.location.assign(`/auth/familysearch/login?returnurl=${encodeURIComponent(window.location.href)}`)
    }
  }, SEND_KEEPALIVE_INTERVAL)

  const EXTENDED_SESSION_TIMEOUT = 3600000 // 1 hour in milliseconds
  KeepAliveTimer = window.setTimeout(() => {
    KeepAliveTimer = null
    window.clearInterval(SendKeepAliveTimer)
    SendKeepAliveTimer = null
  }, EXTENDED_SESSION_TIMEOUT)
}

function PlacesAppProvider({ children }) {
  const [state, dispatch] = useReducer(placesAppReducer, {
    permissions: {},
    editMode: false,
    keepAlive: null,
    sendkeepAlive: null,
    history: { location: { search: '' } },
    ui: {
      resultsAreHidden: false,
      detailsAreHidden: false,
      resultsIsOffScreen: false,
    },
  })

  // Add search state management
  const [searchState, dispatchSearch] = useReducer(searchReducer, DEFAULT_SEARCH_STATE)

  async function fetchPermissions() {
    const permissions = await getPlacePermissions()
    dispatch({ type: 'permissions', permissions })
    return permissions
  }

  async function getPlaceServiceHealth() {
    const health = await getHealth()
    dispatch({ type: 'health', health })
    return health
  }

  useEffect(() => {
    fetchPermissions()
    getPlaceServiceHealth()
  }, [])

  const { permissions, health } = state

  // console.log('permissions:', permissions)
  useEffect(() => {
    if (!permissions || health === undefined) return
    const editMode = hasAnyPermissionForFeature(permissions, 'CAN_EDIT', health)
    dispatch({ type: 'editMode', editMode })
    dispatch({ type: 'authComplete', authComplete: true })
  }, [permissions, health])

  // FIXME - the session keepAlive no longer seems to be working
  const { editMode } = state
  useEffect(() => {
    if (editMode) {
      if (!KeepAliveTimer) {
        keepAliveSession(editMode)
      }
    }
  }, [editMode])

  // Enhanced context value with useMemo to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      ...state,
      searchState,
      dispatchSearch,
      // UI state setters
      setResultsAreHidden: (value) =>
        dispatch({
          type: 'SET_UI_STATE',
          uiState: { resultsAreHidden: value },
        }),
      setDetailsAreHidden: (value) =>
        dispatch({
          type: 'SET_UI_STATE',
          uiState: { detailsAreHidden: value },
        }),
      setResultsIsOffScreen: (value) =>
        dispatch({
          type: 'SET_UI_STATE',
          uiState: { resultsIsOffScreen: value },
        }),
    }),
    [state, searchState, dispatchSearch]
  )

  return (
    <PlacesAppStateContext.Provider value={contextValue}>
      <PlacesAppDispatchContext.Provider value={dispatch}>{children}</PlacesAppDispatchContext.Provider>
    </PlacesAppStateContext.Provider>
  )
}

function usePlacesAppState() {
  const context = useContext(PlacesAppStateContext)
  if (context === undefined) {
    throw new Error('usePlacesAppState must be used within a PlacesAppProvider')
  }
  return context
}

function usePlacesAppDispatch() {
  const context = useContext(PlacesAppDispatchContext)
  if (context === undefined) {
    throw new Error('usePlacesAppDispatch must be used within a PlacesAppProvider')
  }
  return context
}

export { PlacesAppProvider, usePlacesAppState, usePlacesAppDispatch }
